import { useCallback, useEffect, useState } from 'react';

import { subscribeToActiveSession } from '../services/activeSessionPoll';
import {
  ActiveStreamingSession,
  getActiveStreamingSession,
  isSessionInFlight,
} from '../services/streamingService';

const FORCED_LIVE_SESSION_KEY = 'dev.forceLiveSession';
const FORCED_LIVE_INDEX_KEY = 'dev.forceLiveIndex';

// Local development only: `?liveSession=1` (or `=0`) forces the answer, so the live-session UI can
// be worked on without a patient actually streaming.
//
// Gated on the hostname, so the parameter is inert everywhere else — including mercury — and
// cannot be used to fake this state against a real patient.
//
// The value sticks to the browser tab (sessionStorage) rather than being read fresh from the URL
// each time. Reaching the plan editor takes several in-app navigations, most of which rebuild the
// query string from scratch, so a URL-only flag would evaporate somewhere between logging in and
// opening the plan. Set it once anywhere in the app and it holds until the tab closes;
// `?liveSession=0` clears it.
//
// Returns undefined when not forced, which is why the hook below tests for undefined rather than
// falsiness: `false` is a meaningful "force not-live", distinct from "don't force anything".
// Capture the parameter into sessionStorage. Call this at APP STARTUP, not only from the hook:
// the hook has exactly one consumer (the plan editor), so reading the URL there means the
// parameter is only ever seen if it is on the editor's own URL. Reaching the editor takes several
// in-app navigations that rebuild the query string, so `?liveSession=1` set anywhere else — the
// login page, the root — was silently dropped before anything read it, which is the opposite of
// what the comment above promises.
export function captureForcedLiveSession(): void {
  if (!window.location.host.includes('localhost')) {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  // `?liveIndex=N` is the companion flag: it forces "the patient is on slot N", so the per-exercise
  // live markers in the plan editor can be worked on without a patient streaming. Captured the same
  // way and under the same hostname gate as liveSession above.
  const indexFromUrl = params.get('liveIndex');
  if (indexFromUrl !== null) {
    const parsed = Number.parseInt(indexFromUrl, 10);
    if (Number.isInteger(parsed) && parsed > 0) {
      window.sessionStorage.setItem(FORCED_LIVE_INDEX_KEY, String(parsed));
    } else {
      window.sessionStorage.removeItem(FORCED_LIVE_INDEX_KEY);
    }
  }
  const fromUrl = params.get('liveSession');
  if (fromUrl === null) {
    return;
  }
  const value = fromUrl !== '0' && fromUrl !== 'false';
  window.sessionStorage.setItem(FORCED_LIVE_SESSION_KEY, String(value));
}

function useForcedLiveIndex(): number | undefined {
  const [forcedIndex] = useState<number | undefined>(() => {
    if (!window.location.host.includes('localhost')) {
      return undefined;
    }
    captureForcedLiveSession();
    const stored = window.sessionStorage.getItem(FORCED_LIVE_INDEX_KEY);
    if (stored === null) {
      return undefined;
    }
    const parsed = Number.parseInt(stored, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  });
  return forcedIndex;
}

function useForcedLiveSession(): boolean | undefined {
  const [forced] = useState<boolean | undefined>(() => {
    if (!window.location.host.includes('localhost')) {
      return undefined;
    }
    // Still read the URL here too, so the parameter works when it IS on the editor's own URL
    // (a hard reload of the edit page) without depending on startup having run first.
    captureForcedLiveSession();
    const stored = window.sessionStorage.getItem(FORCED_LIVE_SESSION_KEY);
    if (stored === null) {
      return undefined;
    }
    return stored === 'true';
  });
  return forced;
}

// Is this patient streaming right now?
//
// Extracted from ActivePlanCaseSelector, which has run this exact poll in production for a while:
// 10s interval, a cancelled flag so a late response can't write to an unmounted component, and a
// non-fatal catch that leaves the previous answer in place rather than flapping the UI on one
// failed request.
//
// `checkNow` is the pre-flight companion. The poll leaves a window of up to 10 seconds in which a
// session can start without the UI knowing, so anything consequential — saving a plan — should
// re-ask immediately rather than trusting the last tick.
//
// The poll already fetched the whole session, so `sessionId` and `currentPlanIndex` are returned
// rather than discarded: the plan editor needs them to show which exercise the patient has reached,
// and re-requesting the same endpoint from a second hook would double the traffic for data already
// in hand.
export default function useActiveSessionGuard(
  patientId: string | undefined,
  enabled = true,
): {
  isSessionLive: boolean;
  sessionId: string | undefined;
  currentPlanIndex: number | undefined;
  checkNow: () => Promise<boolean>;
} {
  const [isSessionLive, setIsSessionLive] = useState(false);
  const [session, setSession] = useState<ActiveStreamingSession | undefined>();

  const forced = useForcedLiveSession();
  const forcedIndex = useForcedLiveIndex();

  useEffect(() => {
    if (forced !== undefined) {
      setIsSessionLive(forced);
      return;
    }
    if (!patientId || !enabled) {
      return;
    }
    // Shared with the patient header's case selector and the results poller —
    // one request per patient rather than three asking the same question. See
    // services/activeSessionPoll.ts. `checkNow` below is what the callers use
    // before they act, so correctness does not depend on this cadence.
    return subscribeToActiveSession(patientId, ({ session: fetched, inFlight }) => {
      setIsSessionLive(inFlight);
      setSession(fetched);
    });
  }, [patientId, enabled, forced]);

  const checkNow = useCallback(async () => {
    if (forced !== undefined) {
      return forced;
    }
    if (!patientId) {
      return false;
    }
    try {
      const fetched = await getActiveStreamingSession(patientId);
      const live = isSessionInFlight(fetched);
      setIsSessionLive(live);
      setSession(fetched);
      return live;
    } catch {
      // Unknown rather than false. The caller decides what to do with that; treating a failed
      // request as "no session" would silently drop the guard exactly when the backend is
      // unhealthy.
      return isSessionLive;
    }
  }, [patientId, forced, isSessionLive]);

  // Only while the session is actually live. A session that has just ended leaves its last
  // response in state, and reporting the exercise the patient WAS on would leave a stale marker
  // on the plan after they finished.
  const liveSession = isSessionLive ? session : undefined;

  return {
    isSessionLive,
    sessionId: liveSession?.session_id,
    // `?liveIndex=N` wins on localhost so the markers can be developed without a patient; it is
    // undefined everywhere else, including mercury.
    currentPlanIndex: forcedIndex ?? liveSession?.current_movement?.plan_index ?? undefined,
    checkNow,
  };
}
